// The key matcher (R853, ui-map-interface.md §9): one press against the
// bindings of data/presentation/keybindings.toml, which balancegen emits
// as `KEYS` in gen/presentation.ts. A handler names no key of its own:
// it asks which binding a press answers and switches on the id.
//
// A claim is one string: the modifiers in the order ctrl-, meta-, shift-,
// alt-, then the base. The base is the `e.code` name for a digit, Tab,
// Space, Escape, Home, End, Backspace (R2143) and the four arrows of R2083 and R2101, because `alt-2`
// is not `2` and `shift-1` is `!` on most layouts, and a shifted arrow
// must not answer the bare arrow; for every other key it is the printed
// `e.key`.
// The modifier rules the handlers used to spell out — AltGr passes
// through, exactly one of two chords assigns, shift refuses the heat
// chord — all fall out of the claims, because `ctrl-alt-Digit1` and
// `ctrl-shift-Digit1` are nobody's claim.

import type { KeyBinding } from './gen/presentation';

/** The part of a KeyboardEvent the matcher reads, so a test passes a plain object. */
export interface KeyPress {
  readonly key: string;
  readonly code: string;
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
}

/** The keys a claim names by `e.code`; every other key goes by `e.key`. */
const CODE_BASES = /^(Digit[0-9]|Tab|Space|Escape|Home|End|ArrowUp|ArrowDown|ArrowLeft|ArrowRight|Backspace)$/;

/**
 * The three POSITION bases (R1611): a claim of `Comma`, `Minus` or `Equal`
 * reads the key by `e.code`, so it stands at one place on a US and a German
 * layout. A claim of the printed character (`,`, `-`, `=`) still reads
 * `e.key`, so the viewer and the terrain lab keep their keys.
 */
const POSITION_BASES = /^(Comma|Minus|Equal)$/;

/** The position claim of a press, or null for a key that is no position base. */
function positionClaimOf(e: KeyPress): string | null {
  if (!POSITION_BASES.test(e.code)) return null;
  return modifiers(e, e.shiftKey) + e.code;
}

/** The modifiers of a claim in the claim order: ctrl-, meta-, shift-,
 *  alt-. `shift` says whether the claim writes the held shift. */
function modifiers(e: ClickPress, shift: boolean): string {
  let s = '';
  if (e.ctrlKey) s += 'ctrl-';
  if (e.metaKey) s += 'meta-';
  if (shift) s += 'shift-';
  if (e.altKey) s += 'alt-';
  return s;
}

/**
 * The canonical claim of a press. A printed character already carries
 * its shift (`?` is shift-slash), so `shift-` is prefixed for a code base
 * and for a ctrl or meta chord alone; a letter under ctrl or meta is
 * lowercased, so `ctrl-shift-z` reads the same on every platform.
 */
export function claimOf(e: KeyPress): string {
  const code = CODE_BASES.test(e.code);
  const chord = e.ctrlKey || e.metaKey;
  let base = code ? e.code : e.key;
  if (!code && chord && base.length === 1) base = base.toLowerCase();
  return modifiers(e, e.shiftKey && (code || chord)) + base;
}

/**
 * Whether a press answers one binding. An `altgr` binding also takes the
 * press under alt, and under ctrl with alt, because `[` and `]` are AltGr
 * characters on a German layout: AltGr arrives as alt on macOS and as
 * ctrl+alt on Windows and Linux (R456). A plain ctrl chord and every meta
 * chord stay the browser's.
 */
export function matches(b: KeyBinding, e: KeyPress): boolean {
  if (b.altgr) {
    return !e.metaKey && (!e.ctrlKey || e.altKey) && b.claims.includes(e.key);
  }
  if (b.claims.includes(claimOf(e))) return true;
  const pos = positionClaimOf(e);
  return pos !== null && b.claims.includes(pos);
}

/**
 * The id of the binding a press answers, or null. An `altgr` binding is
 * read first, the way the handlers read the layer toggles before the
 * chord bail (R456): on a German macOS layout AltGr+5 prints `[` on the
 * Digit5 key, and the toggle wins over the heat chord `alt-Digit5`.
 * After that, the first binding in table order wins.
 */
export function matchKey<T extends Record<string, KeyBinding>>(
  e: KeyPress, table: T,
): keyof T | null {
  for (const id in table) {
    if (table[id].altgr && matches(table[id], e)) return id;
  }
  for (const id in table) {
    if (!table[id].altgr && matches(table[id], e)) return id;
  }
  return null;
}

/** The part of a pointer event the click matcher reads. */
export interface ClickPress {
  readonly ctrlKey: boolean;
  readonly shiftKey: boolean;
  readonly altKey: boolean;
  readonly metaKey: boolean;
}

/**
 * The canonical claim of a left click: the modifiers in the claim order,
 * then the pointer base `Click` (R1294). A click carries no printed
 * character, so `shift-` is written whenever shift is held.
 */
export function clickClaimOf(e: ClickPress): string {
  return `${modifiers(e, e.shiftKey)}Click`;
}

/**
 * The id of the binding a left click answers, or null. A plain click is
 * nobody's claim: the map grammar of §8 owns it.
 */
export function matchClick<T extends Record<string, KeyBinding>>(
  e: ClickPress, table: T,
): keyof T | null {
  const claim = clickClaimOf(e);
  if (claim === 'Click') return null;
  for (const id in table) {
    if (table[id].claims.includes(claim)) return id;
  }
  return null;
}

/** The digit of a `Digit0`…`Digit9` code, or -1 for every other key. */
export function digitOf(e: KeyPress): number {
  const m = /^Digit([0-9])$/.exec(e.code);
  return m ? Number(m[1]) : -1;
}

/** The cap the reference prints for each named base of a claim. */
const BASE_LABELS: Readonly<Record<string, string>> = {
  Escape: 'esc', Enter: 'enter', Space: 'space', Tab: 'tab', Home: 'home', End: 'end',
  ArrowUp: '↑', ArrowDown: '↓', ArrowLeft: '←', ArrowRight: '→', Backspace: '⌫', Click: 'click',
};

/** A named base at the end of a claim: one of the keys above. */
const NAMED_BASE = new RegExp(`(${Object.keys(BASE_LABELS).join('|')})$`);

/** The cap the reference prints for one claim: `alt-Digit3` → `alt-3`. */
export function claimLabel(claim: string): string {
  return claim.replace(/Digit/, '').replace(NAMED_BASE, (base) => BASE_LABELS[base]);
}
